'use client';
import React, { useEffect, useRef, useState } from 'react';

const stats = [
  { id: 'stat-students', value: 120000, label: 'Students Participated', suffix: '+', prefix: '' },
  { id: 'stat-countries', value: 40, label: 'Countries', suffix: '+', prefix: '' },
  { id: 'stat-schools', value: 3200, label: 'Schools Registered', suffix: '+', prefix: '' },
  { id: 'stat-olympiads', value: 7, label: 'STEM Olympiads', suffix: '', prefix: '' },
];

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCounter({ stat, start }: { stat: typeof stats[0]; start: boolean }) {
  const count = useCountUp(stat.value, 2000, start);
  return (
    <div className="text-center">
      <p className="stat-counter text-4xl xl:text-5xl text-white mb-2">
        {stat.prefix}{count.toLocaleString()}{stat.suffix}
      </p>
      <p className="text-sm font-medium text-slate-300">{stat.label}</p>
    </div>
  );
}

export default function GlobalStats() {
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 bg-hero-gradient relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-80 h-80 rounded-full bg-secondary blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-accent blur-3xl" />
      </div>
      <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <h2 className="text-3xl xl:text-4xl font-extrabold text-white mb-4">
            A Growing Global Community
          </h2>
          <p className="text-slate-300 max-w-xl mx-auto">
            OnlineOlympiads.org is trusted by students, parents, and schools worldwide.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((stat) => (
            <StatCounter key={stat.id} stat={stat} start={started} />
          ))}
        </div>
      </div>
    </section>
  );
}